import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, Navigate, useNavigate } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import {
  MDBNavbar,
  MDBContainer,
  MDBNavbarBrand,
  MDBNavbarNav,
  MDBNavbarItem,
  MDBNavbarLink,
  MDBBtn,
  MDBIcon
} from 'mdb-react-ui-kit';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Home from './pages/Home';
import Range from './pages/Range';
import ManualEntry from './pages/ManualEntry';
import Sessions from './pages/Sessions';
import SessionDetails from './pages/SessionDetails';
import Settings from './pages/Settings';
import Login from './pages/Login';
import AuthCallback from './pages/AuthCallback';
import PendingApproval from './pages/PendingApproval';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import './App.css';

// Google OAuth Client ID
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '140907181156-pc45cqu5a092ulf6g6b1hba2bjqi2000.apps.googleusercontent.com';

function App() {
  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <Router>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </Router>
    </GoogleOAuthProvider>
  );
}

function AppContent() {
  const { isAuthenticated, isLoading } = useAuth();
  
  const [theme, setTheme] = useState<'light' | 'dark'>(() => {
    const savedTheme = localStorage.getItem('theme');
    return (savedTheme as 'light' | 'dark') || 'light';
  });

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prev => prev === 'light' ? 'dark' : 'light');
  };

  if (isLoading) {
    return (
      <div className="App">
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <Navigation theme={theme} />
      <main className="main-content">
        <Routes>
          <Route path="/login" element={!isAuthenticated ? <Login /> : <Navigate to="/" replace />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/pending-approval" element={isAuthenticated ? <PendingApproval /> : <Navigate to="/login" replace />} />
          
          {/* Public routes */}
          <Route path="/" element={<Home theme={theme} />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          
          {/* Protected routes */}
          <Route path="/range" element={<ProtectedRoute><Range theme={theme} /></ProtectedRoute>} />
          <Route path="/manual-entry" element={<ProtectedRoute><ManualEntry theme={theme} /></ProtectedRoute>} />
          <Route path="/sessions" element={<ProtectedRoute><Sessions theme={theme} /></ProtectedRoute>} />
          <Route path="/session-details/:id" element={<ProtectedRoute><SessionDetails theme={theme} /></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute><Settings theme={theme} onThemeToggle={toggleTheme} /></ProtectedRoute>} />
        </Routes>
      </main>
    </div>
  );
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, approvalStatus } = useAuth();

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Redirect to pending approval page if user is not approved
  if (approvalStatus !== 'approved') {
    return <Navigate to="/pending-approval" replace />;
  }

  return <>{children}</>;
}

function Navigation({ theme }: { theme: 'light' | 'dark' }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signOut, isAuthenticated } = useAuth();
  
  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const handleSignOut = async () => {
    if (window.confirm('Are you sure you want to sign out?')) {
      await signOut();
    }
  };

  return (
    <MDBNavbar expand='lg' light bgColor='white' className='shadow-sm mb-3'>
      <MDBContainer fluid>
        <MDBNavbarBrand tag={Link} to='/'>
          <img src='/SolidZero.png' alt='SolidRange' height='65' className='me-2 logo-transparent' />
          <span className='fw-bold'>SolidRange</span>
        </MDBNavbarBrand>
        
        <MDBNavbarNav className='d-flex flex-row align-items-center ms-auto'>
          <MDBNavbarItem className='me-2'>
            <MDBNavbarLink tag={Link} to='/' active={isActive('/')}>
              <MDBIcon fas icon='home' className='me-1' /> Home
            </MDBNavbarLink>
          </MDBNavbarItem>
          
          {isAuthenticated && (
            <>
              <MDBNavbarItem className='me-2'>
                <MDBNavbarLink tag={Link} to='/range' active={isActive('/range')}>
                  <MDBIcon fas icon='bullseye' className='me-1' /> Range
                </MDBNavbarLink>
              </MDBNavbarItem>
              <MDBNavbarItem className='me-2'>
                <MDBNavbarLink tag={Link} to='/sessions' active={isActive('/sessions')}>
                  <MDBIcon fas icon='chart-line' className='me-1' /> Sessions
                </MDBNavbarLink>
              </MDBNavbarItem>
              <MDBNavbarItem className='me-3'>
                <MDBNavbarLink tag={Link} to='/settings' active={isActive('/settings')}>
                  <MDBIcon fas icon='cog' className='me-1' /> Settings
                </MDBNavbarLink>
              </MDBNavbarItem>
              
              <MDBNavbarItem className='d-flex align-items-center me-3'>
                {user?.picture && (
                  <img 
                    src={user.picture} 
                    alt={user.displayName || 'User'} 
                    className='rounded-circle me-2'
                    width='32'
                    height='32'
                  />
                )}
                <small className='text-muted'>{user?.displayName || user?.email || 'User'}</small>
              </MDBNavbarItem>
              
              <MDBNavbarItem>
                <MDBBtn color='danger' size='sm' onClick={handleSignOut}>
                  <MDBIcon fas icon='sign-out-alt' className='me-1' /> Sign Out
                </MDBBtn>
              </MDBNavbarItem>
            </>
          )}
          
          {!isAuthenticated && (
            <MDBNavbarItem>
              <MDBBtn color='success' size='sm' onClick={() => navigate('/login')}>
                <MDBIcon fas icon='sign-in-alt' className='me-1' /> Sign In
              </MDBBtn>
            </MDBNavbarItem>
          )}
        </MDBNavbarNav>
      </MDBContainer>
    </MDBNavbar>
  );
}

export default App;
